import React from "react";
import ReactSwitch from "react-switch";
import { useSnapshot } from "valtio";
import { useTranslation } from "react-i18next";
import { SettingsIcon, StyledIconButton } from "@aureskonnect/react-ui";

import {
    computedStore,
    designStore,
    setIsPageActionsButtonsActive,
    setPageActionsButtonsItems,
    setActionsButtonsItems,
    setIsActionButtonsActive,
    setIsSubStepActivated,
    setSelectedSubStep,
    setActiveTabIndex,
    initAccountFideliteSubStep,
    generalConfigStore,
} from "@store";
import { ACCOUNT_CREATION_ID, ACCOUNT_CREATION_ITEM } from "@constants/index";

const CREATE_ACCOUNT_SWITCH_TESTID =
    "authentication-modes-create-account-switch";
// Préfixe de data-testid des switches de boutons d'action. Composant partagé
// (Connexion, Paiement, Modes d'authentification, etc.) → on suffixe par item.id
// (UUID stable) pour garantir l'unicité du testid dans tout src/.
const ACTION_BUTTON_SWITCH_TESTID_PREFIX = "action-button-switch";
// Index de l'onglet "Authentication modes" dans la page Fidélité : l'engrenage
// "Créer un compte" y bascule pour afficher la sous-étape de réglage.
const AUTHENTICATION_MODES_DESIGN_TAB_INDEX = 2;

type ActionsButtonsPropsType = {
    localActionsButtonsItems: ActionsButtonsDesignItemType[];
    setLocalActionsButtonsItems: React.Dispatch<
        React.SetStateAction<ActionsButtonsDesignItemType[]>
    >;
};

export function ActionsButtonsWrapper({
    localActionsButtonsItems,
    setLocalActionsButtonsItems,
}: ActionsButtonsPropsType): JSX.Element {
    const { t } = useTranslation();

    const { isActionButtonsActive, activePageName } = useSnapshot(designStore);
    const { elements } = useSnapshot(computedStore);
    const {
        project: {
            template: {
                pages: { ways },
            },
        },
    } = useSnapshot(generalConfigStore);
    const FinalMessageIndex = elements.find(
        (element) => element.name === "finalMessage"
    )?.index;

    const isDisabled =
        FinalMessageIndex !== undefined
            ? ways[activePageName].name === "printerOptions" &&
              ways[activePageName].index === FinalMessageIndex - 1
            : false;

    function handleActivateSwitchOnClickEvent(name: string) {
        const copOfData = [...localActionsButtonsItems];
        if (
            copOfData.filter((actionButtonItem) => {
                return actionButtonItem.active === true;
            }).length === 0 &&
            ways[activePageName].name !== "printerOptions" &&
            isActionButtonsActive === false
        ) {
            setIsActionButtonsActive(true);
            setIsPageActionsButtonsActive(true, ways[activePageName].name);
        }

        const findIndex = copOfData.find((element) => element.name === name);

        if (findIndex) {
            const actionButtonItem = copOfData.indexOf(findIndex);

            copOfData[actionButtonItem] = {
                ...findIndex,
                active: !copOfData[actionButtonItem].active,
            };
            setLocalActionsButtonsItems(copOfData);
            setActionsButtonsItems(copOfData);
            setPageActionsButtonsItems(copOfData, ways[activePageName].name);
        }

        if (
            copOfData.filter((actionButtonItem) => {
                return actionButtonItem.active === true;
            }).length === 0 &&
            ways[activePageName].name !== "printerOptions"
        ) {
            setIsActionButtonsActive(false);
            setIsPageActionsButtonsActive(false, ways[activePageName].name);
        }
    }

    return (
        <div>
            <div
                className="mt-4 rounded border"
                style={{
                    width: "93%",
                    height: "96%",
                    borderColor: "#CECECE",
                }}
            >
                <div
                    className="text-left px-4 d-flex align-items-center cursor__clz"
                    style={{
                        backgroundColor: "#EDEDED",
                        height: "40px",
                        fontSize: "20px",
                    }}
                >
                    {t("Actions Buttons")}
                </div>
                <div className="p-4">
                    {localActionsButtonsItems.map(
                        (item: ActionsButtonsDesignItemType, index: number) => {
                            return (
                                <div
                                    className="py-2 switch-items__clz cursor__clz"
                                    style={
                                        item.id === ACCOUNT_CREATION_ID
                                            ? {
                                                  // texte | engrenage | switch :
                                                  // engrenage et switch poussés à
                                                  // droite, switch aligné en fin
                                                  // comme les autres lignes.
                                                  gridTemplateColumns:
                                                      "1fr 1fr auto",
                                                  alignItems: "center",
                                              }
                                            : undefined
                                    }
                                    key={item.id}
                                >
                                    <span>{t(item?.name)}</span>
                                    {item.id === ACCOUNT_CREATION_ID ? (
                                        <span data-testid="authentication-modes-create-account-settings-btn">
                                            <StyledIconButton
                                                icon="SettingsIcon"
                                                className="m-0"
                                                aria-label={t(
                                                    "Configure the create an account step"
                                                )}
                                                onClick={() => {
                                                    // Initialise la sous-étape
                                                    // "accountFidelite" depuis le
                                                    // template (comme Connexion)
                                                    // avant de l'ouvrir dans
                                                    // l'onglet Modes. Si absente,
                                                    // on n'ouvre rien.
                                                    if (
                                                        !initAccountFideliteSubStep()
                                                    ) {
                                                        return;
                                                    }
                                                    setSelectedSubStep(
                                                        ACCOUNT_CREATION_ITEM
                                                    );
                                                    setIsSubStepActivated(true);
                                                    setActiveTabIndex(
                                                        AUTHENTICATION_MODES_DESIGN_TAB_INDEX
                                                    );
                                                }}
                                            >
                                                <SettingsIcon
                                                    height={25}
                                                    width={25}
                                                />
                                            </StyledIconButton>
                                        </span>
                                    ) : null}
                                    <span
                                        data-testid={
                                            item.id === ACCOUNT_CREATION_ID
                                                ? CREATE_ACCOUNT_SWITCH_TESTID
                                                : `${ACTION_BUTTON_SWITCH_TESTID_PREFIX}-${item.id}`
                                        }
                                    >
                                        <ReactSwitch
                                            uncheckedIcon={false}
                                            checkedIcon={false}
                                            handleDiameter={26}
                                            offColor="#f7b4b8"
                                            offHandleColor="#E30613"
                                            onChange={() => {
                                                handleActivateSwitchOnClickEvent(
                                                    localActionsButtonsItems[
                                                        index
                                                    ].name
                                                );
                                            }}
                                            checked={
                                                localActionsButtonsItems[index]
                                                    .active
                                            }
                                            onColor="#c2eddd"
                                            onHandleColor="#34C38F"
                                            width={50}
                                            height={20}
                                            disabled={isDisabled}
                                            className={`${
                                                item.name === "Ignore"
                                                    ? "cgt_icn_Ignore"
                                                    : item.name === "Back"
                                                    ? "cgt_icn_back"
                                                    : item.name === "giveUp"
                                                    ? "cgt_icn_giveUp"
                                                    : ""
                                            }`}
                                        />
                                    </span>
                                </div>
                            );
                        }
                    )}
                </div>
            </div>
        </div>
    );
}
